import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, createBlock as _createBlock, withCtx as _withCtx, unref as _unref } from "vue"

const _hoisted_1 = { class: "app-avatar-editor" }
const _hoisted_2 = { class: "app-avatar-editor__upload-section-logo" }
const _hoisted_3 = { class: "app-avatar-editor__upload-section-title" }
const _hoisted_4 = {
  key: 1,
  class: "app-avatar-editor__editor-container"
}
const _hoisted_5 = { class: "app-avatar-editor__editor-container-preview-bar" }
const _hoisted_6 = { class: "app-avatar-editor__editor-container-preview-bar-circle" }
const _hoisted_7 = ["src"]
const _hoisted_8 = { class: "app-avatar-editor__editor-container-preview-bar-file-name" }
const _hoisted_9 = { class: "app-avatar-editor__editor-container-toolbar" }
const _hoisted_10 = { class: "app-avatar-editor__editor-container-toolbar-block" }
const _hoisted_11 = { class: "app-avatar-editor__editor-container-toolbar-block" }
const _hoisted_12 = { class: "app-avatar-editor__editor-container-toolbar-block-rotate" }
const _hoisted_13 = {
  class: "app-avatar-editor__editor-container-toolbar-block",
  style: { flex: 1 }
}
const _hoisted_14 = { class: "app-avatar-editor__editor-container-cropper-area" }
const _hoisted_15 = { class: "app-avatar-editor__editor-container-save-section" }

import {
	ref,
	computed,
	nextTick
} from 'vue';
import { Cropper } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import AppButton from "@/components/ui-kit/button/AppButton.vue";
import FileInput from "@/components/file/FileInput.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import {FileApi} from "@/ api/FileApi";

type FlipState = {
	horizontal: number;
	vertical: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AppAvatarEditor',
  emits: ["save", "error"],
  setup(__props, { emit: __emit }) {

const emit = __emit;

const isLoading = ref(false);
const $inputDegRef = ref<InstanceType<typeof FileInput> | null>(null);
const $fileInput = ref<HTMLInputElement | null>(null);
const $cropperRef = ref<InstanceType<typeof Cropper> | null>(null);
const imageSrc = ref<string | null>(null);
const originalImageSrc = ref<string | null>(null);
const fileName = ref<string>('');
const currentPreview = ref<string | null>(null);
const flipState = ref<FlipState>({ horizontal: 1, vertical: 1 });
const isCropperReady = ref<boolean>(false);
const handleDegValue = ref(0)
const isHandleDegValueEdited = ref(false)

const previewImage = computed<string | null>(() => {
	return currentPreview.value || imageSrc.value;
});

const triggerFileInput = (): void => {
	$fileInput.value?.click();
};

const handleFileUpload = (event: Event): void => {
	const input = event.target as HTMLInputElement;
	const file = input.files?.[0];
	if (!file) return;

	fileName.value = file.name;

	const reader = new FileReader();
	reader.onload = (e: ProgressEvent<FileReader>) => {
		const src = e.target?.result as string;
		imageSrc.value = src;
		originalImageSrc.value = src;
		flipState.value = { horizontal: 1, vertical: 1 };
		isCropperReady.value = false;
	};
	reader.readAsDataURL(file);

	input.value = '';
};

// Обработчик готовности кроппера
const handleCropperReady = (): void => {
	isCropperReady.value = true;
};

// Обработчик изменений в кроппере
const handleCropperChange = ({ canvas }: { canvas: HTMLCanvasElement | null }): void => {
	if (canvas) {
		currentPreview.value = canvas.toDataURL();
	}
};

// Поворот влево
const rotateLeft = (): void => {
	if ($cropperRef.value && isCropperReady.value) {
		$cropperRef.value.rotate(-90);
	}
};

// Поворот вправо
const rotateRight = (): void => {
	if ($cropperRef.value && isCropperReady.value) {
		$cropperRef.value.rotate(90);
	}
};

// Поворот изображения на указанное количество градусов по часовой стрелке
const rotateByDegrees = (degrees: string): void => {
	if ($cropperRef.value && isCropperReady.value) {
		$cropperRef.value.rotate(parseInt(degrees))
	}
};

// Отражение по горизонтали
const flipHorizontal = (): void => {
	flipState.value.horizontal *= -1;
	refreshImage();
};

// Отражение по вертикали
const flipVertical = (): void => {
	flipState.value.vertical *= -1;
	refreshImage();
};

// Обновление изображения с учетом отражения
const refreshImage = (): void => {
	if (!$cropperRef.value || !originalImageSrc.value) return;

	isCropperReady.value = false;

	const img = new Image();
	img.crossOrigin = 'anonymous';
	img.onload = () => {
		const canvas = document.createElement('canvas');
		canvas.width = img.width;
		canvas.height = img.height;

		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		ctx.translate(
			flipState.value.horizontal === 1 ? 0 : canvas.width,
			flipState.value.vertical === 1 ? 0 : canvas.height
		);
		ctx.scale(flipState.value.horizontal, flipState.value.vertical);
		ctx.drawImage(img, 0, 0);

		imageSrc.value = canvas.toDataURL('image/png');
	};

	img.src = originalImageSrc.value;
};

// Увеличенеи зума
const zoomIn = (): void => {
	if ($cropperRef.value && isCropperReady.value) {
		$cropperRef.value.zoom(1.2);
	}
};

// Уменьшение зума
const zoomOut = (): void => {
	if ($cropperRef.value && isCropperReady.value) {
		$cropperRef.value.zoom(0.8);
	}
};

// Сброс всех изменений
const resetAll = (): void => {
	if ($cropperRef.value && originalImageSrc.value && isCropperReady.value) {
		imageSrc.value = originalImageSrc.value;
		flipState.value = { horizontal: 1, vertical: 1 };
		$cropperRef.value.reset();
		handleDegValue.value = 0
		isHandleDegValueEdited.value = false
	}
};

// Удаление картинки
const dropImg = () => {
	imageSrc.value = null
	currentPreview.value = null
}

// Сохранение изображения в виде Blob
const saveImage = async (): Promise<void> => {
	if (!$cropperRef.value || !isCropperReady.value) {
		emit('error', new Error('Ошибка инициализации обработчика аватаров'));
		return;
	}

	try {
		isLoading.value = true;
		const { canvas } = $cropperRef.value.getResult();

		if (!canvas) {
			emit('error', new Error('Ошибка доступа к canvas'));
			return;
		}

		const blob = await new Promise<Blob | null>((resolve) => {
			canvas.toBlob(resolve, 'image/png');
		});

		if (!blob) {
			emit('error', new Error('Ошибка при формировании изображения'));
			return;
		}

		const formData = new FormData();
		formData.append('File', blob);
		formData.append('Filename', fileName.value);
		formData.append('Type', '0');
		const result = await FileApi.PhotoUpload(formData);
		emit('save', result);

	} catch (error) {
		emit(
			'error',
			error instanceof Error
				? error
				: new Error('Что-то пошло не так...')
		);
	} finally {
		isLoading.value = false;
	}
};

const editHandleDeg = () => {
	isHandleDegValueEdited.value = true;
	nextTick(() => $inputDegRef.value.focus())
}

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!imageSrc.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "app-avatar-editor__upload-section",
          onClick: triggerFileInput
        }, [
          _createElementVNode("div", _hoisted_2, [
            _createVNode(AppIcon, {
              class: "app-avatar-editor__upload-section-logo-icon",
              name: "photo-add",
              width: "30px",
              color: "#979797",
              "hover-color": "#3F884F"
            })
          ]),
          _createElementVNode("div", _hoisted_3, _toDisplayString(_ctx.$t("ui_choose_file")), 1),
          _createElementVNode("input", {
            ref_key: "$fileInput",
            ref: $fileInput,
            type: "file",
            accept: "image/*",
            onChange: handleFileUpload,
            style: { display: 'none' }
          }, null, 544)
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_4, [
          (isLoading.value)
            ? (_openBlock(), _createBlock(AppLoader, {
                key: 0,
                style: _normalizeStyle(loaderStyle.value)
              }, null, 8, ["style"]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              (previewImage.value)
                ? (_openBlock(), _createElementBlock("img", {
                    key: 0,
                    src: previewImage.value,
                    alt: "Preview"
                  }, null, 8, _hoisted_7))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_8, _toDisplayString(fileName.value), 1),
            _createVNode(AppIcon, {
              class: "app-avatar-editor__editor-container-preview-bar-icon",
              name: "delete-outline",
              width: "18px",
              color: "#979797",
              "hover-color": "#BA4C4C",
              cursor: "pointer",
              onClick: dropImg
            })
          ]),
          _createElementVNode("div", _hoisted_9, [
            _createElementVNode("div", _hoisted_10, [
              _createVNode(AppTooltip, {
                text: _ctx.$t('ui_flip_horizontal')
              }, {
                activator: _withCtx(() => [
                  _createVNode(AppIcon, {
                    name: "flip-vertical",
                    width: "20px",
                    color: "#979797",
                    "hover-color": "#3F884F",
                    cursor: "pointer",
                    onClick: flipHorizontal
                  })
                ]),
                _: 1
              }, 8, ["text"]),
              _createVNode(AppTooltip, {
                text: _ctx.$t('ui_flip_vertical')
              }, {
                activator: _withCtx(() => [
                  _createVNode(AppIcon, {
                    name: "flip-horizontal",
                    width: "20px",
                    color: "#979797",
                    "hover-color": "#3F884F",
                    cursor: "pointer",
                    onClick: flipVertical
                  })
                ]),
                _: 1
              }, 8, ["text"])
            ]),
            _createElementVNode("div", _hoisted_11, [
              _createVNode(AppTooltip, {
                text: _ctx.$t('ui_rotate_left_90')
              }, {
                activator: _withCtx(() => [
                  _createVNode(AppIcon, {
                    name: "rotate-left",
                    width: "20px",
                    color: "#979797",
                    "hover-color": "#3F884F",
                    cursor: "pointer",
                    onClick: rotateLeft
                  })
                ]),
                _: 1
              }, 8, ["text"]),
              _createVNode(AppTooltip, {
                text: _ctx.$t('ui_rotate_right_90')
              }, {
                activator: _withCtx(() => [
                  _createVNode(AppIcon, {
                    name: "rotate-right",
                    width: "20px",
                    color: "#979797",
                    "hover-color": "#3F884F",
                    cursor: "pointer",
                    onClick: rotateRight
                  })
                ]),
                _: 1
              }, 8, ["text"]),
              _createElementVNode("div", _hoisted_12, [
                _createVNode(AppTooltip, {
                  text: _ctx.$t('ui_rotate_degrees')
                }, {
                  activator: _withCtx(() => [
                    _createVNode(AppIcon, {
                      name: "park-outline_rotation",
                      width: "15px",
                      height: "18px",
                      color: "#979797",
                      "hover-color": "#3F884F",
                      cursor: "pointer",
                      onClick: editHandleDeg
                    })
                  ]),
                  _: 1
                }, 8, ["text"]),
                (!isHandleDegValueEdited.value)
                  ? (_openBlock(), _createElementBlock("div", {
                      key: 0,
                      class: "app-avatar-editor__editor-container-toolbar-block-rotate-input",
                      onClick: editHandleDeg
                    }, _toDisplayString(handleDegValue.value), 1))
                  : (_openBlock(), _createBlock(FileInput, {
                      key: 1,
                      ref_key: "$inputDegRef",
                      ref: $inputDegRef,
                      modelValue: handleDegValue.value,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((handleDegValue).value = $event)),
                      onEnter: _cache[1] || (_cache[1] = ($event: any) => (rotateByDegrees(parseInt($event))))
                    }, null, 8, ["modelValue"]))
              ])
            ]),
            _createElementVNode("div", _hoisted_13, [
              _createVNode(AppTooltip, {
                text: _ctx.$t('ui_zoom_in')
              }, {
                activator: _withCtx(() => [
                  _createVNode(AppIcon, {
                    name: "zoom-in",
                    width: "20px",
                    color: "#979797",
                    "hover-color": "#3F884F",
                    cursor: "pointer",
                    onClick: zoomIn
                  })
                ]),
                _: 1
              }, 8, ["text"]),
              _createVNode(AppTooltip, {
                text: _ctx.$t('ui_zoom_out')
              }, {
                activator: _withCtx(() => [
                  _createVNode(AppIcon, {
                    name: "zoom-out",
                    width: "20px",
                    color: "#979797",
                    "hover-color": "#3F884F",
                    cursor: "pointer",
                    onClick: zoomOut
                  })
                ]),
                _: 1
              }, 8, ["text"])
            ]),
            _createElementVNode("span", {
              class: "app-avatar-editor__editor-container-toolbar-drop",
              onClick: resetAll
            }, _toDisplayString(_ctx.$t("selector_reset")), 1)
          ]),
          _createElementVNode("div", _hoisted_14, [
            _createVNode(_unref(Cropper), {
              ref_key: "$cropperRef",
              ref: $cropperRef,
              class: "app-avatar-editor__editor-container-cropper",
              src: imageSrc.value,
              "stencil-props": {
						aspectRatio: 1
					},
              "resize-image": {
						wheel: true,
						touch: true,
						adjustStencil: false
					},
              onChange: handleCropperChange,
              onReady: handleCropperReady
            }, null, 8, ["src"])
          ]),
          _createElementVNode("div", _hoisted_15, [
            _createVNode(AppButton, {
              label: _ctx.$t('save'),
              onClick: saveImage,
              disabled: isLoading.value
            }, null, 8, ["label", "disabled"])
          ])
        ]))
  ]))
}
}

})